/*
 * Phase 4A Commit 4Af-fas — page-store-locator.js
 *
 * Public stockist locator (theme/sections/page-store-locator.liquid).
 * Fetches the locked PublicStockist projection from
 * GET /apps/dashboard/find-a-store and renders cards grouped by city,
 * with client-side filtering by province + city + subbrand chip +
 * search string.
 *
 * Auth: NONE. The endpoint is anonymous-public; Shopify's App Proxy
 * adds the HMAC signature so the backend can verify the request came
 * from the storefront. No tokens, no cookies, no headers needed.
 *
 * Two empty states (intentional ≠ broken):
 *   - No stockists at all (pre-launch): "Stockists coming soon."
 *   - No matches after filtering: "No stockists match those filters."
 *     + a "Clear filters" affordance.
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  var FIND_A_STORE_URL = '/apps/dashboard/find-a-store';

  // ─── DOM handles (all in the page-store-locator.liquid shell) ────

  var heroCountEl   = $('[data-loc-hero-count]');
  var provinceSel   = $('[data-loc-province]');
  var citySel       = $('[data-loc-city]');
  var subBtns       = $$('[data-loc-sub]');
  var searchInput   = $('[data-loc-q]');
  var clearBtn      = $('[data-loc-clear]');
  var summaryEl     = $('[data-loc-summary]');
  var resultsEl     = $('[data-loc-results-container]');
  var emptyEl       = $('[data-loc-empty]');
  var launchingEl   = $('[data-loc-launching]');
  var clearBtn2     = $('[data-loc-clear-2]');

  // ─── State ───────────────────────────────────────────────────────

  /** Full list from the backend. Never mutated after fetch. */
  var stockists = [];
  var filters = {
    province: 'All',
    city: 'All',
    subbrand: 'All',
    q: '',
  };

  // ─── Escape helper (XSS-safe text insertion) ─────────────────────

  function escapeHtml(s) {
    if (s === null || s === undefined) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function buildMapsUrl(s) {
    var parts = [s.display_name, s.address_line_1, s.city, s.province]
      .filter(function (p) { return p && String(p).trim() !== ''; })
      .join(' ');
    return 'https://www.google.com/maps/search/' + encodeURIComponent(parts);
  }

  // ─── Render: hero counter ────────────────────────────────────────

  function renderHeroCount(total) {
    if (!heroCountEl) return;
    var label = total === 1 ? 'location' : 'locations';
    heroCountEl.textContent = 'Store locator · ' + total + ' ' + label + ' across canada';
  }

  // ─── Render: filter dropdowns (province + cascading city) ────────

  function uniqueSorted(arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var v = arr[i];
      if (v && !seen[v]) { seen[v] = true; out.push(v); }
    }
    out.sort();
    return out;
  }

  function populateProvinceDropdown() {
    if (!provinceSel) return;
    var provinces = uniqueSorted(stockists.map(function (s) { return s.province; }));
    provinceSel.innerHTML =
      '<option value="All">All provinces</option>' +
      provinces.map(function (p) {
        return '<option value="' + escapeHtml(p) + '">' + escapeHtml(p) + '</option>';
      }).join('');
    provinceSel.value = filters.province;
  }

  function populateCityDropdown() {
    if (!citySel) return;
    var inProvince = filters.province === 'All'
      ? stockists
      : stockists.filter(function (s) { return s.province === filters.province; });
    var cities = uniqueSorted(inProvince.map(function (s) { return s.city; }));
    citySel.innerHTML =
      '<option value="All">All cities</option>' +
      cities.map(function (c) {
        return '<option value="' + escapeHtml(c) + '">' + escapeHtml(c) + '</option>';
      }).join('');
    // Reset city if the previously selected city isn't in the new province.
    if (filters.city !== 'All' && cities.indexOf(filters.city) === -1) {
      filters.city = 'All';
    }
    citySel.value = filters.city;
  }

  // ─── Filter logic (pure; runs client-side over the full list) ────

  function matchesFilters(s) {
    if (filters.province !== 'All' && s.province !== filters.province) return false;
    if (filters.city !== 'All' && s.city !== filters.city) return false;
    if (filters.subbrand !== 'All') {
      if (!Array.isArray(s.carried_subbrands)) return false;
      if (s.carried_subbrands.indexOf(filters.subbrand) === -1) return false;
    }
    if (filters.q) {
      var q = filters.q.toLowerCase();
      var haystack = [
        s.display_name,
        s.city,
        s.address_line_1,
        Array.isArray(s.neighborhood_tags) ? s.neighborhood_tags.join(' ') : '',
      ].join(' ').toLowerCase();
      if (haystack.indexOf(q) === -1) return false;
    }
    return true;
  }

  function anyFilterActive() {
    return (
      filters.province !== 'All' ||
      filters.city !== 'All' ||
      filters.subbrand !== 'All' ||
      filters.q !== ''
    );
  }

  // ─── Render: a single store card (matches the original Liquid) ───

  function cardHtml(s) {
    var subs = Array.isArray(s.carried_subbrands) ? s.carried_subbrands : [];
    var tagsHtml = subs.map(function (b) {
      return '<span class="locator-card-tag ' + escapeHtml(String(b).toLowerCase()) + '">' + escapeHtml(b) + '</span>';
    }).join('');
    var addr2 = s.address_line_2 ? escapeHtml(s.address_line_2) + '<br>' : '';
    var note = s.public_descriptor ? escapeHtml(s.public_descriptor) : '';
    return (
      '<article class="locator-card" data-loc-card>' +
        '<div class="locator-card-head">' +
          '<span class="locator-card-type">' + escapeHtml(s.business_type || 'Other') + '</span>' +
          '<div class="locator-card-tags">' + tagsHtml + '</div>' +
        '</div>' +
        '<h4 class="locator-card-name">' + escapeHtml(s.display_name) + '</h4>' +
        '<p class="locator-card-addr">' + escapeHtml(s.address_line_1) + '<br>' + addr2 + escapeHtml(s.city) + ', ' + escapeHtml(s.province) + '</p>' +
        '<div class="locator-card-foot">' +
          '<span>' + note + '</span>' +
          '<a class="locator-card-directions" href="' + escapeHtml(buildMapsUrl(s)) + '" target="_blank" rel="noopener">Directions &uarr;</a>' +
        '</div>' +
      '</article>'
    );
  }

  function cityGroupHtml(cityLabel, list) {
    return (
      '<div class="locator-city-group" data-loc-group>' +
        '<div class="locator-city-head">' +
          '<h3 class="serif-display" style="font-style:italic;font-size:clamp(28px,3vw,44px);letter-spacing:-0.02em;">' + escapeHtml(cityLabel) + '</h3>' +
        '</div>' +
        '<div class="locator-grid">' +
          list.map(cardHtml).join('') +
        '</div>' +
      '</div>'
    );
  }

  // ─── Render: results + counter + empty states ────────────────────

  function render() {
    if (!resultsEl) return;

    // Total list never changes after fetch — but two empty states do.
    if (stockists.length === 0) {
      // "Stockists coming soon" — pre-launch / no published accounts yet.
      resultsEl.innerHTML = '';
      if (launchingEl) launchingEl.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      if (summaryEl) summaryEl.textContent = '0 locations';
      if (clearBtn) clearBtn.hidden = true;
      return;
    }
    if (launchingEl) launchingEl.hidden = true;

    var filtered = stockists.filter(matchesFilters);
    if (summaryEl) {
      summaryEl.textContent = filtered.length + ' of ' + stockists.length + ' location' + (stockists.length === 1 ? '' : 's');
    }
    if (clearBtn) clearBtn.hidden = !anyFilterActive();

    if (filtered.length === 0) {
      resultsEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;

    // Group by city → "City, Province" headers in stable order
    // (stockists arrive ordered province, city, display_name from the
    // backend; preserve that here via a Map-like dedupe).
    var groupOrder = [];
    var groups = {};
    for (var i = 0; i < filtered.length; i++) {
      var s = filtered[i];
      var label = s.city + ', ' + s.province;
      if (!groups[label]) { groups[label] = []; groupOrder.push(label); }
      groups[label].push(s);
    }
    resultsEl.innerHTML = groupOrder.map(function (label) {
      return cityGroupHtml(label, groups[label]);
    }).join('');
  }

  // ─── Render: error state ─────────────────────────────────────────

  function renderError(err) {
    if (!resultsEl) return;
    resultsEl.innerHTML =
      '<div class="locator-empty" style="padding:80px 0;text-align:center;">' +
        '<p class="caption" style="color:var(--fg-mute);">Couldn’t load stockists. Please refresh.</p>' +
      '</div>';
    if (summaryEl) summaryEl.textContent = '';
    if (clearBtn) clearBtn.hidden = true;
    if (emptyEl) emptyEl.hidden = true;
    if (launchingEl) launchingEl.hidden = true;
    // eslint-disable-next-line no-console
    console.error('[find-a-store] fetch failed:', err);
  }

  // ─── Wiring: filters ─────────────────────────────────────────────

  function wireFilters() {
    if (provinceSel) {
      provinceSel.addEventListener('change', function () {
        filters.province = provinceSel.value;
        filters.city = 'All';
        populateCityDropdown();
        render();
      });
    }
    if (citySel) {
      citySel.addEventListener('change', function () {
        filters.city = citySel.value;
        render();
      });
    }
    subBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        subBtns.forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        filters.subbrand = btn.getAttribute('data-loc-sub') || 'All';
        render();
      });
    });
    if (searchInput) {
      var debounceTimer = null;
      searchInput.addEventListener('input', function () {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          filters.q = searchInput.value.trim();
          render();
        }, 120);
      });
    }
    if (clearBtn) clearBtn.addEventListener('click', resetFilters);
    if (clearBtn2) clearBtn2.addEventListener('click', resetFilters);
  }

  function resetFilters() {
    filters.province = 'All';
    filters.city = 'All';
    filters.subbrand = 'All';
    filters.q = '';
    if (provinceSel) provinceSel.value = 'All';
    populateCityDropdown();
    if (searchInput) searchInput.value = '';
    subBtns.forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-loc-sub') === 'All');
    });
    render();
  }

  // ─── Fetch + init ───────────────────────────────────────────────

  function init() {
    if (!resultsEl) return;
    // Show a transient loading hint (will be overwritten by render()).
    resultsEl.innerHTML = '<div class="caption" style="color:var(--fg-mute);padding:24px 0;">Loading stockists…</div>';

    fetch(FIND_A_STORE_URL, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (body) {
        var list = (body && Array.isArray(body.stockists)) ? body.stockists : [];
        stockists = list;
        renderHeroCount(stockists.length);
        populateProvinceDropdown();
        populateCityDropdown();
        wireFilters();
        render();
      })
      .catch(function (err) {
        renderError(err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
